import React, { useState, useRef } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stack,
  Alert,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Link,
} from '@mui/material'
import {
  CloudUpload as UploadIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Download as DownloadIcon,
} from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import { InventoryService } from '../../services/inventoryService'
import { StockImportResult } from '../../types/inventory'

interface BulkStockImportDialogProps {
  open: boolean
  onClose: () => void
  onSuccess: () => void
}

const BulkStockImportDialog: React.FC<BulkStockImportDialogProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [importResult, setImportResult] = useState<StockImportResult | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { enqueueSnackbar } = useSnackbar()

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      if (!file.name.endsWith('.csv')) {
        enqueueSnackbar('Please select a CSV file', { variant: 'error' })
        return
      }
      setSelectedFile(file)
      setImportResult(null)
    }
  }

  const handleUpload = async () => {
    if (!selectedFile) return

    setIsUploading(true)
    try {
      const result = await InventoryService.importStockData(selectedFile)
      setImportResult(result)
      
      if (result.failed_imports === 0) {
        enqueueSnackbar('Stock data imported successfully', { variant: 'success' })
      } else if (result.successful_imports > 0) {
        enqueueSnackbar(
          `Import completed with ${result.failed_imports} errors`,
          { variant: 'warning' }
        )
      } else {
        enqueueSnackbar('Import failed - please check the errors', { variant: 'error' })
      }
    } catch (error: any) {
      enqueueSnackbar(
        error.response?.data?.detail || 'Failed to import stock data',
        { variant: 'error' }
      )
    } finally {
      setIsUploading(false)
    }
  }

  const handleClose = () => {
    if (importResult && importResult.successful_imports > 0) {
      onSuccess()
    }
    setSelectedFile(null)
    setImportResult(null)
    onClose()
  }

  const downloadTemplate = () => {
    const csvContent = 'sku,new_stock,reason\nEXAMPLE-SKU-001,100,Initial stock\nEXAMPLE-SKU-002,50,Restock'
    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'stock_import_template.csv'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  }

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { 
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        }
      }}
    >
      <DialogTitle
        sx={{
          background: 'linear-gradient(135deg, #2196f3 0%, #0d47a1 100%)',
          color: 'white',
          borderRadius: '12px 12px 0 0',
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          Bulk Stock Import
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9 }}>
          Import stock levels from a CSV file for multiple products
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3}>
          {/* Instructions */}
          <Alert severity="info">
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>CSV Format Requirements:</strong>
            </Typography>
            <Typography variant="body2" component="div">
              • Required columns: <code>sku</code>, <code>new_stock</code>, <code>reason</code>
              <br />
              • SKU must match existing variant SKUs
              <br />
              • new_stock must be a non-negative integer
              <br />
              • reason is required for audit purposes
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Link
                component="button"
                variant="body2"
                onClick={downloadTemplate}
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
              >
                <DownloadIcon fontSize="small" />
                Download Template
              </Link>
            </Box>
          </Alert>

          {/* File Upload */}
          <Box
            sx={{
              border: '2px dashed',
              borderColor: selectedFile ? 'success.main' : 'grey.300',
              borderRadius: 2,
              p: 3,
              textAlign: 'center',
              bgcolor: selectedFile ? 'success.50' : 'grey.50',
              cursor: 'pointer',
              transition: 'all 0.2s',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'primary.50',
              },
            }}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            
            <UploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            
            {selectedFile ? (
              <Box>
                <Typography variant="body1" fontWeight={600} color="success.main">
                  {selectedFile.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {(selectedFile.size / 1024).toFixed(1)} KB • Click to change file
                </Typography>
              </Box>
            ) : (
              <Box>
                <Typography variant="body1" fontWeight={600}>
                  Click to select CSV file
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Or drag and drop your file here
                </Typography>
              </Box>
            )}
          </Box>

          {/* Upload Progress */}
          {isUploading && (
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Importing stock data...
              </Typography>
              <LinearProgress />
            </Box>
          )}

          {/* Import Results */}
          {importResult && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Import Results
              </Typography>
              
              <Stack spacing={2}>
                {/* Summary */}
                <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Stack direction="row" spacing={3}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Total Rows
                      </Typography>
                      <Typography variant="h6">
                        {importResult.total_rows}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Successful
                      </Typography>
                      <Typography variant="h6" color="success.main">
                        {importResult.successful_imports}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Failed
                      </Typography>
                      <Typography variant="h6" color="error.main">
                        {importResult.failed_imports}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>

                {/* Errors */}
                {importResult.errors.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" color="error.main" sx={{ mb: 1 }}>
                      Errors ({importResult.errors.length})
                    </Typography>
                    <List dense sx={{ maxHeight: 200, overflow: 'auto' }}>
                      {importResult.errors.slice(0, 10).map((error, index) => (
                        <ListItem key={index}>
                          <ListItemIcon>
                            <ErrorIcon color="error" fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary={error.error}
                            secondary={
                              error.row
                                ? `Row ${error.row}${error.sku ? ` • SKU: ${error.sku}` : ''}`
                                : error.sku
                                ? `SKU: ${error.sku}`
                                : undefined
                            }
                          />
                        </ListItem>
                      ))}
                      {importResult.errors.length > 10 && (
                        <ListItem>
                          <ListItemText
                            primary={`... and ${importResult.errors.length - 10} more errors`}
                            sx={{ fontStyle: 'italic', color: 'text.secondary' }}
                          />
                        </ListItem>
                      )}
                    </List>
                  </Box>
                )}

                {/* Success Message */}
                {importResult.successful_imports > 0 && (
                  <Alert severity="success">
                    Successfully imported {importResult.successful_imports} stock updates
                  </Alert>
                )}
              </Stack>
            </Box>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 3, gap: 1 }}>
        <Button 
          onClick={handleClose}
          variant="outlined"
          sx={{ 
            textTransform: 'none',
            fontWeight: 500,
            borderRadius: 2,
          }}
        >
          {importResult ? 'Close' : 'Cancel'}
        </Button>
        {!importResult && (
          <Button
            onClick={handleUpload}
            variant="contained"
            color="secondary"
            disabled={!selectedFile || isUploading}
            sx={{ 
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              minWidth: 140,
            }}
          >
            {isUploading ? 'Importing...' : 'Import Stock Data'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default BulkStockImportDialog